import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  FileCheck2, Plus, Trash2, RefreshCw, ExternalLink, Send, Copy, Check, DollarSign, FileText, Pencil,
} from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import LetterheadManager from './LetterheadManager';
import SendDocumentDialog from './SendDocumentDialog';
import StructuredInvoiceForm from './StructuredInvoiceForm';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso || '—'; } };

const statusStyle = (s) => {
  const map = {
    unpaid: 'bg-red-600/20 text-red-300 border-red-600/40',
    partial: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    paid: 'bg-green-600/20 text-green-300 border-green-600/40',
  };
  return map[s] || map.unpaid;
};

const PaymentDialog = ({ open, onClose, invoice, onSaved }) => {
  const [form, setForm] = useState({ date: todayISO(), amount: '', method: 'Cash', note: '', auto_log_to_ledger: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setForm((f) => ({ ...f, date: todayISO(), amount: String(invoice.balance_due || ''), method: 'Cash', note: '', auto_log_to_ledger: true }));
    }
  }, [open, invoice]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/invoices/${invoice.id}/payment`,
        { ...form, amount: amt },
        { headers: authHeaders() },
      );
      toast.success(`Recorded ${usd(amt)} — invoice ${data.payment_status}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="payment-dialog-title">Record payment · {invoice.invoice_number}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {invoice.customer_name} · Balance due: <span className="text-amber-300 font-bold">{usd(invoice.balance_due)}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-gray-300 text-xs">Date</Label>
            <Input data-testid="payment-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} required className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Amount ($)</Label>
            <Input data-testid="payment-amount" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required className="bg-slate-800 border-slate-700 text-white tabular-nums" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
              <SelectTrigger data-testid="payment-method" className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-white">
                {['Cash', 'Check', 'Card', 'Bank transfer', 'Other'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Note (optional)</Label>
            <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Check #1234, deposit, etc." className="bg-slate-800 border-slate-700 text-white text-sm" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              data-testid="payment-auto-ledger"
              checked={form.auto_log_to_ledger}
              onChange={(e) => setForm((f) => ({ ...f, auto_log_to_ledger: e.target.checked }))}
              className="rounded border-slate-600 bg-slate-800 text-red-600 focus:ring-red-600"
            />
            Auto-log this payment as Income on the Ledger (recommended)
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 text-gray-300 hover:bg-slate-800">Cancel</Button>
            <Button type="submit" data-testid="payment-submit" disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
              {saving ? 'Recording…' : `Record ${form.amount ? usd(parseFloat(form.amount) || 0) : 'payment'}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminInvoices = ({ onUnauthorized }) => {
  const [invoices, setInvoices] = useState([]);
  const [letterheads, setLetterheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [paying, setPaying] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [lhManagerOpen, setLhManagerOpen] = useState(false);
  const [sending, setSending] = useState(null);
  const [editing, setEditing] = useState(null);  // null = closed; {} = new; object = edit existing

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, lh] = await Promise.all([
        axios.get(`${API}/api/invoices`, { headers: authHeaders() }),
        axios.get(`${API}/api/letterheads`, { headers: authHeaders() }),
      ]);
      setInvoices(inv.data);
      setLetterheads(lh.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  // Listen for cross-tab "open payment dialog" event from AdminEstimates after conversion
  useEffect(() => {
    const handler = (ev) => {
      const id = ev.detail;
      if (id) {
        // Re-fetch then open dialog for that invoice
        load().then(() => {
          setTimeout(() => {
            setInvoices((prev) => {
              const found = prev.find((i) => i.id === id);
              if (found) setPaying(found);
              return prev;
            });
          }, 100);
        });
      }
    };
    window.addEventListener('invoice:open-payment', handler);
    return () => window.removeEventListener('invoice:open-payment', handler);
  }, [load]);

  const visible = filter === 'all' ? invoices : invoices.filter((i) => i.payment_status === filter);
  const stats = invoices.reduce((acc, i) => {
    acc.outstanding += i.balance_due || 0;
    acc.collected += i.paid_amount || 0;
    return acc;
  }, { outstanding: 0, collected: 0 });

  const remove = async (inv) => {
    const ok = window.confirm(
      `Delete ${inv.invoice_number}?\n\n` +
      `This will also remove ${(inv.payments || []).length} payment record(s) and their auto-logged ledger entries. This cannot be undone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/invoices/${inv.id}`, { headers: authHeaders() });
      toast.success('Invoice deleted');
      load();
    } catch (err) {
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  const publicUrl = (inv) => `${window.location.origin}/invoice/${inv.id}`;

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(publicUrl(inv));
      setCopiedId(inv.id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const emailLink = (inv) => {
    const subject = encodeURIComponent(`TayLord Arms — Invoice ${inv.invoice_number}`);
    const body = encodeURIComponent(
      `Hi ${inv.customer_name},\n\n` +
      `Your invoice is ready:\n${publicUrl(inv)}\n\n` +
      `Amount due: ${usd(inv.balance_due)}\n\n` +
      `Thanks,\nTayLord Arms LLC`
    );
    return `mailto:${inv.customer_email || ''}?subject=${subject}&body=${body}`;
  };

  const changeLetterhead = async (inv, lhId) => {
    // lhId is "" when user picks "Default — TayLord Arms branded"
    try {
      await axios.patch(
        `${API}/api/invoices/${inv.id}`,
        { letterhead_id: lhId || '' },
        { headers: authHeaders() },
      );
      toast.success(lhId ? 'Letterhead updated' : 'Reverted to default letterhead');
      load();
    } catch (err) {
      toast.error(`Update failed: ${apiErr(err)}`);
    }
  };

  return (
    <section data-testid="admin-invoices-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-green-400" /> Invoices
          </h2>
          <p className="text-sm text-gray-400">Convert accepted estimates into invoices. Record payments to auto-log income.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setEditing({})} size="sm" data-testid="invoice-new" className="bg-red-600 hover:bg-red-700 text-white">
            <Plus className="w-3 h-3 mr-2" /> New invoice
          </Button>
          <Button onClick={() => setLhManagerOpen(true)} variant="outline" size="sm" data-testid="invoice-manage-letterheads" className="border-slate-700 text-white hover:bg-slate-800">
            <FileText className="w-3 h-3 mr-2" /> Manage letterheads
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
            <RefreshCw className="w-3 h-3 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="bg-slate-900 border-slate-800" data-testid="invoices-stat-count">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400">Total invoices</div>
            <div className="text-2xl font-bold text-white mt-1 tabular-nums">{invoices.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="invoices-stat-outstanding">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400">Outstanding balance</div>
            <div className="text-2xl font-bold text-amber-300 mt-1 tabular-nums">{usd(stats.outstanding)}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="invoices-stat-collected">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-gray-400">Collected to date</div>
            <div className="text-2xl font-bold text-green-300 mt-1 tabular-nums">{usd(stats.collected)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base text-white">All invoices ({invoices.length})</CardTitle>
            <div className="inline-flex rounded border border-slate-700 overflow-hidden">
              {['all', 'unpaid', 'partial', 'paid'].map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`invoices-filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`text-xs px-3 py-1 capitalize ${filter === f ? 'bg-red-600 text-white' : 'bg-slate-900 text-gray-400 hover:bg-slate-800'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-400" data-testid="invoices-loading">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-gray-500 py-8 text-center" data-testid="invoices-empty">
              {invoices.length === 0
                ? 'No invoices yet. Convert an accepted estimate to create your first invoice.'
                : 'No invoices match this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/40 text-gray-400 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Number</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">From estimate</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Letterhead</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                    <th className="px-3 py-2 font-medium text-right">Balance</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((inv) => (
                    <tr key={inv.id} className="border-t border-slate-800 hover:bg-slate-900/70" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-3 py-3 font-mono text-green-300 whitespace-nowrap">{inv.invoice_number}</td>
                      <td className="px-3 py-3">
                        <div className="text-white font-medium">{inv.customer_name}</div>
                        {inv.customer_email && <div className="text-xs text-red-400">{inv.customer_email}</div>}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-amber-300">{inv.estimate_number || '—'}</td>
                      <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{fmt(inv.created_at)}</td>
                      <td className="px-3 py-3">
                        <Badge variant="secondary" className={`${statusStyle(inv.payment_status)} capitalize font-semibold`} data-testid={`invoice-status-${inv.id}`}>
                          {inv.payment_status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3">
                        <Select value={inv.letterhead_id || 'default'} onValueChange={(v) => changeLetterhead(inv, v === 'default' ? '' : v)}>
                          <SelectTrigger data-testid={`invoice-letterhead-${inv.id}`} className="h-7 text-xs bg-slate-800 border-slate-700 text-white w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-white">
                            <SelectItem value="default">Default branded</SelectItem>
                            {letterheads.map((lh) => (
                              <SelectItem key={lh.id} value={lh.id}>{lh.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-white tabular-nums">{usd(inv.total)}</td>
                      <td className={`px-3 py-3 text-right font-bold tabular-nums ${inv.balance_due > 0 ? 'text-amber-300' : 'text-green-300'}`}>
                        {usd(inv.balance_due)}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {inv.payment_status !== 'paid' && (
                            <Button
                              size="sm"
                              data-testid={`invoice-pay-${inv.id}`}
                              onClick={() => setPaying(inv)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <DollarSign className="w-3 h-3 mr-1" /> Record payment
                            </Button>
                          )}
                          <Button size="sm" variant="outline" data-testid={`invoice-edit-${inv.id}`} onClick={() => setEditing(inv)} title="Edit invoice" className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2">
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`invoice-view-${inv.id}`} asChild className="border-slate-700 text-gray-200 hover:bg-slate-800">
                            <a href={publicUrl(inv)} target="_blank" rel="noopener noreferrer" title="Open public invoice">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`invoice-copy-${inv.id}`} onClick={() => copyLink(inv)} title="Copy public link" className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2">
                            {copiedId === inv.id ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`invoice-email-${inv.id}`} onClick={() => setSending(inv)} title="Email invoice with PDF attachment" className="border-slate-700 text-gray-300 hover:bg-slate-800 px-2">
                            <Send className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)} className="border-slate-700 text-gray-400 hover:bg-red-950/40 hover:text-red-400 px-2" aria-label="Delete invoice">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaymentDialog
        open={Boolean(paying)}
        onClose={() => setPaying(null)}
        invoice={paying}
        onSaved={load}
      />

      <LetterheadManager
        open={lhManagerOpen}
        onClose={() => setLhManagerOpen(false)}
        onChanged={load}
      />

      <SendDocumentDialog
        open={Boolean(sending)}
        onClose={() => setSending(null)}
        kind="invoice"
        doc={sending}
        onSent={load}
      />

      <StructuredInvoiceForm
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        initial={editing && editing.id ? editing : null}
        letterheads={letterheads}
        onSaved={load}
      />
    </section>
  );
};

export default AdminInvoices;
